import { motion } from 'framer-motion';
import { Frame } from 'lucide-react';

const CURRENCY_SYMBOL = { EUR: '€', USD: '$', GBP: '£', PLN: 'zł' };
function formatPrice(amount, currency) {
  if (amount == null || amount === '') return null;
  const sym = CURRENCY_SYMBOL[currency] || '';
  const n = Number(amount);
  if (Number.isNaN(n)) return null;
  if (currency === 'PLN') return `${n.toLocaleString('pl-PL', { minimumFractionDigits: 0 })} ${sym}`;
  return `${sym}${n.toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
}

/**
 * Premium artwork card used on /artworks, artist pages, and any grid.
 * - Subtle white hue behind the image so transparent-PNG artworks separate
 *   from the black canvas.
 * - On hover: tiny translateY + scale + soft shadow. Respects
 *   `prefers-reduced-motion` via framer-motion's built-in respect.
 */
export default function ArtworkCard({ artwork, testId }) {
  const price = formatPrice(artwork.price_amount, artwork.currency);
  const soldOut = !!artwork.is_sold_out;
  const media = artwork.thumbnail_url || artwork.media_url;
  const artist = artwork.artist?.name || artwork.artist_name;

  return (
    <motion.a
      href={`/artworks/${artwork.slug}`}
      data-testid={testId || `artwork-card-${artwork.slug}`}
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      whileHover={{
        y: -3,
        scale: 1.012,
        transition: { duration: 0.35, ease: [0.22, 0.61, 0.36, 1] },
      }}
      className="group block relative"
    >
      <div className="relative aspect-[4/5] overflow-hidden border border-[#111]">
        {/* Soft glow faded in via OPACITY (compositor-only). Animating the
            box-shadow value directly repainted every frame and caused the
            hover lag — a static shadow on a fading layer is GPU-cheap. */}
        <div
          aria-hidden
          className="absolute -inset-px pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{ boxShadow: '0 18px 40px -16px rgba(144,224,239,0.18)' }}
        />
        {/* Subtle white hue backing plate to lift transparent-PNG artworks */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-40 group-hover:opacity-70 transition-opacity duration-500"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.02) 45%, rgba(255,255,255,0) 72%)',
          }}
        />
        {media ? (
          <img
            src={media}
            alt={artwork.title}
            loading="lazy"
            className="relative w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
          />
        ) : (
          <div className="relative w-full h-full flex items-center justify-center bg-[#0a0a0a]">
            <Frame className="w-6 h-6 text-[#333]" />
          </div>
        )}

        {soldOut && (
          <div className="absolute top-3 right-3 bg-[#050505]/90 border border-[#c89dff]/40 px-2.5 py-1 text-[9px] font-mono uppercase tracking-[0.24em] text-[#c89dff]">
            Sold out
          </div>
        )}
      </div>

      <div className="mt-3">
        {artist && (
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#90E0EF] group-hover:text-white transition-colors">
            {artist}
          </div>
        )}
        <h3 className="mt-1 text-[14px] font-outfit text-white leading-snug">
          {artwork.title}
        </h3>
        <div className="mt-1.5 flex items-center justify-between text-[11px] font-mono">
          <span className="text-[#888]">
            {[artwork.year, artwork.dimensions].filter(Boolean).join(' · ') || ' '}
          </span>
          {price && (
            <span className="text-[#90E0EF]">
              {soldOut ? '—' : price}
            </span>
          )}
        </div>
      </div>
    </motion.a>
  );
}
